import * as yup from 'yup'
import { IntlShape, defineMessages } from 'react-intl'
import { isNumber } from 'lodash'
import { ICabinSetting } from 'features/InventoryRules/components/CabinStrategiesForm/types'
import { IDaysToDepartureSetting } from 'features/InventoryRules/components/DaysToDepartureSettingsForm/types'

const messages = defineMessages({
  required: {
    id: 'dictionary.required',
    defaultMessage: 'Required'
  },
  invalid: {
    id: 'dictionary.invalid',
    defaultMessage: 'Invalid'
  },
  invalidRange: {
    id: 'dictionary.rangeInvalid',
    defaultMessage: 'Invalid range'
  }
})

const companyMessages = defineMessages({
  required: {
    id: 'inventoryRules.form.company.required',
    defaultMessage: 'The company is required'
  }
})

const nameMessages = defineMessages({
  required: {
    id: 'inventoryRules.form.name.required',
    defaultMessage: 'The name is required'
  },
  max: {
    id: 'inventoryRules.form.name.max',
    defaultMessage: 'The name can be up to 100 characters'
  }
})

const descriptionMessages = defineMessages({
  max: {
    id: 'inventoryRules.form.descriptions.max',
    defaultMessage: 'The description can be up to 100 characters'
  }
})

const originMessages = defineMessages({
  required: {
    id: 'inventoryRules.form.origin.required',
    defaultMessage: 'The origin is required'
  }
})

const destinationMessages = defineMessages({
  required: {
    id: 'inventoryRules.form.destination.required',
    defaultMessage: 'The destination is required'
  }
})

const segmentsMessages = defineMessages({
  required: {
    id: 'inventoryRules.form.segments.required',
    defaultMessage: 'You need to select at least one valid segment'
  }
})

const routeServicesMessages = defineMessages({
  required: {
    id: 'inventoryRules.form.routeServices.required',
    defaultMessage: 'You need to select at least one service'
  }
})

const loadFactorMessages = defineMessages({
  max: {
    id: 'inventoryRules.form.loadFactor.max',
    defaultMessage: 'The value can not exceed 100%'
  }
})

const cabinSettingsSchema = (intl: IntlShape): yup.SchemaOf<ICabinSetting[]> => {
  return yup.array().of(
    yup
      .object()
      .shape({
        bookingClassId: yup.string().required(),
        name: yup.string().required(),
        priority: yup.number().required(),
        isActive: yup.boolean().required(),
        promo: yup.boolean(),
        maxLoadFactor: yup
          .number()
          .required(intl.formatMessage(messages.required))
          .max(100, intl.formatMessage(loadFactorMessages.max))
      })
      .test('is-load-factor-valid', 'test-msg', function (cabinSetting) {
        const array = this.parent
        const itemIndex = parseInt(this.path.split('cabinSettings[')[1].split(']')[0], 10)
        const isLastCabinSetting = itemIndex === array.length - 1
        const currentMaxLoadFactor = cabinSetting.maxLoadFactor

        if (!isLastCabinSetting && isNumber(currentMaxLoadFactor)) {
          const nextCabinSetting = array[itemIndex + 1]
          const nextMaxLoadFactor = nextCabinSetting.maxLoadFactor

          if (nextMaxLoadFactor > currentMaxLoadFactor) {
            return this.createError({
              path: `${this.path}.maxLoadFactor`,
              message: intl.formatMessage(messages.invalid)
            })
          }
        }

        return true
      })
  )
}

const daysToDepartureSettingsSchema = (intl: IntlShape): yup.SchemaOf<IDaysToDepartureSetting[]> => {
  return yup.array().of(
    yup
      .object()
      .shape({
        start: yup.number().required(intl.formatMessage(messages.required)),
        end: yup.number().required(intl.formatMessage(messages.required)),
        cabinStrategyIndex: yup.number().required(intl.formatMessage(messages.required))
      })
      .test('is-start-and-end-valid', 'test-msg', function (daysToDepartureSetting) {
        const array = this.parent
        const itemIndex = parseInt(this.path.split('daysToDepartureSettings[')[1].split(']')[0], 10)
        const isFirstSetting = itemIndex === 0
        const currentStartValue = daysToDepartureSetting.start
        const currentEndValue = daysToDepartureSetting.end

        if (!isFirstSetting && isNumber(currentStartValue)) {
          const previousSetting = array[itemIndex - 1] as IDaysToDepartureSetting
          const previousEndValue = previousSetting.end
          const startIsAfterEnd = isNumber(currentEndValue) && currentStartValue >= currentEndValue
          const startIsBeforePreviousEnd = isNumber(previousEndValue) && currentStartValue <= previousEndValue
          if (startIsAfterEnd || startIsBeforePreviousEnd) {
            return this.createError({
              path: `${this.path}.start`,
              message: intl.formatMessage(messages.invalidRange)
            })
          }
        }

        return true
      })
  )
}

export const schema = (intl: IntlShape): TSFixMe => {
  return yup.object().shape({
    company: yup.object().shape({
      id: yup.string().required(intl.formatMessage(companyMessages.required))
    }),
    name: yup
      .string()
      .required(intl.formatMessage(nameMessages.required))
      .max(100, intl.formatMessage(nameMessages.max)),
    description: yup.string().max(100, intl.formatMessage(descriptionMessages.max)),
    departureAtRanges: yup.array().of(
      yup.object().shape({
        startAt: yup.string().required(intl.formatMessage(messages.required)),
        endAt: yup.string().required(intl.formatMessage(messages.required))
      })
    ),
    activationAtRanges: yup.array().of(
      yup.object().shape({
        startAt: yup.string().required(intl.formatMessage(messages.required)),
        endAt: yup.string().required(intl.formatMessage(messages.required))
      })
    ),
    origin: yup.object().shape({
      id: yup.string().required(intl.formatMessage(originMessages.required))
    }),
    destination: yup.object().shape({
      id: yup.string().required(intl.formatMessage(destinationMessages.required))
    }),
    segments: yup.array().min(1, intl.formatMessage(segmentsMessages.required)),
    routeServices: yup.array().min(1, intl.formatMessage(routeServicesMessages.required)),
    cabinStrategies: yup.array().of(
      yup.object().shape({
        isDefault: yup.boolean().required(),
        cabinSettings: cabinSettingsSchema(intl)
      })
    ),
    daysToDepartureSettings: daysToDepartureSettingsSchema(intl)
  })
}
